import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { BriefEntry, BriefEntryVariables } from '__generated__/BriefEntry';
import { BriefSchema, DesignerSource } from '__generated__/types';
import { onReject } from 'common/utils/Bugsnag';
import { trackResumedBriefEvent } from 'projects/events';
import AppendStructuredBriefDesigners from 'projects/mutations/AppendStructuredBriefDesigners';
import CopyBrief from 'projects/mutations/CopyBrief';
import CreateBrief, { useCreateBrief } from 'projects/mutations/CreateBrief';
import BriefEntryPage from 'projects/routes/BriefEntryPage';
import qs from 'qs';
import React, { useState } from 'react';
import { Query } from 'react-apollo';
import { RouteComponentProps } from 'react-router-dom';
import { BriefFieldsRedirect, routes } from '.';
import query from './BriefEntry.graphql';
import { history } from './BrowserRouter';

interface QueryParams {
    designerId?: number;
    category?: string;
}

export const parseQueryString = (query: string): QueryParams => {
    // react router is dumb and includes the `?` in the query string,
    // which qs adds to the first key name. YOU HAD ONE JOB!
    if (query[0] == '?') {
        query = query.slice(1);
    }
    const { designerId, category } = qs.parse(query);
    return {
        designerId: designerId ? parseInt(designerId) : undefined,
        category,
    };
};

function BriefEntryComponent(props: RouteComponentProps) {
    const [isRunningMutation, setIsRunningMutation] = useState(false);
    const [createBrief, { loading: loadingCreateBrief, data }] = useCreateBrief();
    const { designerId, category } = parseQueryString(props.location.search);

    if (data) {
        return <BriefFieldsRedirect brief={data.createStructuredBrief} />;
    }

    return (
        <Query<BriefEntry, BriefEntryVariables>
            query={query}
            variables={{
                id: designerId,
                schemas: [
                    BriefSchema.PREZI,
                    BriefSchema.PROJECT,
                    BriefSchema.SQUARESPACE,
                    BriefSchema.SQUARESPACE_API,
                ],
            }}
        >
            {({ loading, error, data }) => {
                if (loading) {
                    return <LoadableElement variations={['fade-in']} isLoading={true} />;
                }

                if (error) {
                    return <CreateBrief fields={{ project: { category } }} />;
                }

                if (data) {
                    const variables = {
                        designerIds: data.designer ? [data.designer.id] : undefined,
                        fields: { project: { category } },
                    };

                    if (!data.viewer.user || (!data.drafts.length && !data.launched.length)) {
                        return <CreateBrief {...variables} />;
                    }

                    return (
                        <AppendStructuredBriefDesigners>
                            {appendDesigners => (
                                <CopyBrief>
                                    {copyBrief => {
                                        if (loadingCreateBrief || isRunningMutation) {
                                            return (
                                                <LoadableElement
                                                    variations={['fade-in']}
                                                    isLoading={true}
                                                />
                                            );
                                        }

                                        return (
                                            <BriefEntryPage
                                                designer={data.designer}
                                                recentDrafts={data.drafts}
                                                recentLaunchedBriefs={data.launched}
                                                createBriefAction={() => {
                                                    createBrief({ variables });
                                                }}
                                                copyBriefAction={async brief => {
                                                    setIsRunningMutation(true);

                                                    const res = await copyBrief({
                                                        id: brief.id,
                                                        designerIds: variables.designerIds,
                                                    });

                                                    if (res) {
                                                        history.push(routes.brief(res.data!.brief));
                                                    }
                                                }}
                                                resumeBriefAction={brief => {
                                                    trackResumedBriefEvent({
                                                        id: brief.id,
                                                    });
                                                    if (designerId) {
                                                        setIsRunningMutation(true);

                                                        appendDesigners({
                                                            id: brief.id,
                                                            designerIds: [designerId],
                                                            source: DesignerSource.DIRECT,
                                                        }).then(
                                                            () => history.push(routes.brief(brief)),
                                                            onReject('BriefEntry Component')
                                                        );
                                                    } else {
                                                        history.push(routes.brief(brief));
                                                    }
                                                }}
                                                user={data.viewer.user!}
                                            />
                                        );
                                    }}
                                </CopyBrief>
                            )}
                        </AppendStructuredBriefDesigners>
                    );
                }
                return null;
            }}
        </Query>
    );
}

export default BriefEntryComponent;
